import JWPlayer from '@jwplayer/jwplayer-react';
import initJWPlayerMux from '@mux/mux-data-jwplayer';
import { MutableRefObject, useCallback, useMemo } from 'react';

import { LaunchSegment, MuxOrigin, MuxProductType } from '../datasource/generated';
import { useGetMuxConfigQuery } from '../datasource/queries/clientConfig';
import logger from '../utility/logger';
import { parseMuxConfiguration } from '../utility/mux';
import { useAppSelector } from '.';
import { useDeferredData } from './useDeferredData';
import { usePlaybackOptimization } from './usePlaybackOptimization';

/**
 * Mux Data dimension used to split every built-in quality-of-experience metric — Rebuffer
 * Percentage, Rebuffer Count, Time To First Frame, Playback Failure Percentage — by which playback
 * path the viewer got. This is what makes the rollout measurable without adding any new telemetry
 * pipeline.
 *
 * Live carries its own value rather than falling in with `baseline`. Live runs on the Amazon IVS
 * provider and is excluded from the optimization entirely, so folding it into the baseline cohort
 * would compare on-demand-optimized against a mixture of on-demand-baseline and live — different
 * engines with different quality-of-experience profiles, and a misleading result.
 */
export const PLAYBACK_VARIANT_OPTIMIZED = 'optimized';
export const PLAYBACK_VARIANT_BASELINE = 'baseline';
export const PLAYBACK_VARIANT_EXCLUDED_LIVE = 'excluded-live';

export const useMux = () => {
    const { data: muxConfigData } = useGetMuxConfigQuery();
    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const individualSK = useAppSelector((state) => state.user.individualSK);
    const organizationSK = useAppSelector((state) => state.user.organizationSK);
    const clientConfig = useAppSelector((state) => state.clientConfig);
    const { isOptimized } = usePlaybackOptimization();

    const playbackVariant = launchDetails.isWebcast
        ? PLAYBACK_VARIANT_EXCLUDED_LIVE
        : (isOptimized ? PLAYBACK_VARIANT_OPTIMIZED : PLAYBACK_VARIANT_BASELINE);

    const deferredMuxConfig = useDeferredData(
        useMemo(() => muxConfigData ? parseMuxConfiguration(muxConfigData, {
            origin: MuxOrigin.WATCH,
            productType: launchDetails.isWebcast ? MuxProductType.LIVE : MuxProductType.ON_DEMAND,
        }) : null, [launchDetails.isWebcast, muxConfigData])
    );
    const deferredClientConfig = useDeferredData(clientConfig ?? null);

    const initializeMux = useCallback(async (playerRef: MutableRefObject<typeof JWPlayer>, segment?: LaunchSegment) => {
        const muxConfig = await deferredMuxConfig.unwrap();
        const clientConfig = await deferredClientConfig.unwrap();

        if (!muxConfig.isEnabled || !initJWPlayerMux) return;

        const playerInitTime = initJWPlayerMux.utils.now();
        logger.log('Mux initialized', { playerInitTime });
        initJWPlayerMux(playerRef.current, {
            debug: process.env.NODE_ENV !== 'production',
            data: {
                env_key: muxConfig.envKey,

                // Site Metadata
                viewer_user_id: `${individualSK}`,

                custom_1: `${organizationSK}`,
                custom_2: playbackVariant,

                // Metadata
                player_name: 'Watch player',
                player_version: clientConfig.pliVersion,
                player_init_time: playerInitTime,

                // Video Metadata
                video_id: segment?.itemPk ?? launchDetails?.itemPk,
                video_title: segment?.title ?? launchDetails?.title,
                video_series: launchDetails?.title,
                video_stream_type: launchDetails?.isWebcast ? 'live' : 'on-demand',
                video_cdn: segment?.video?.cdn ?? launchDetails?.video?.cdn,
            }
        }, playerRef.current);
    }, [
        deferredMuxConfig,
        deferredClientConfig,
        individualSK,
        organizationSK,
        launchDetails?.isWebcast,
        launchDetails?.itemPk,
        launchDetails?.title,
        launchDetails?.video?.cdn,
        playbackVariant]);

    const emitVideoChangeEvent = useCallback(async (playerRef: MutableRefObject<typeof JWPlayer>, segment: LaunchSegment) => {
        const muxConfig = await deferredMuxConfig.unwrap();

        if (!muxConfig?.isEnabled || !initJWPlayerMux) {
            return;
        }

        playerRef.current.mux.emit('videochange', {
            video_id: segment?.itemPk ?? launchDetails?.itemPk,
            video_title: segment?.title ?? launchDetails?.title,
            video_series: launchDetails?.title,
            video_stream_type: launchDetails?.isWebcast ? 'live' : 'on-demand',
            video_cdn: segment?.video?.cdn ?? launchDetails?.video?.cdn,
            custom_2: playbackVariant,
        });
    }, [deferredMuxConfig, launchDetails?.itemPk, launchDetails?.title, launchDetails?.isWebcast, launchDetails?.video?.cdn, playbackVariant]);

    return {
        initializeMux,
        emitVideoChangeEvent,
    };
};